import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Users, AlertTriangle, ArrowRight, Clock, TrendingUp, ClipboardCheck, FileText, UserPlus, Plus, BookOpen, Target } from 'lucide-react';
import { getModuleIcon, MODULES, getFamiliesForModule, getAllowedTiersForGroup, humanizeFamilyId } from '../../../content';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { PageLayout } from '../../shared/PageLayout';
import { EmptyState } from '../../../components/shared/EmptyState';
import { StudentData } from './StudentDetailPanel';
import { DjayNarrator } from '../../../mascot';
import { getWeeklyActivityForLearner } from '../../../utils/activityHelpers';
import { getAvatarGradient } from '../utils/avatarUtils';
import { V11_CONFIG } from '../../../mastery';
import { getFamilyMasteryKey } from '../../../mastery';
import { getStudentInsights, DifferentiationInsight } from '../utils/differentiationInsights';
import { GroupId } from '../../../config/systemConfig';
import { formatRelativeTime } from '../../../utils/formatters';
import { containerVariants, itemVariants, listContainerVariants, listItemVariants } from '../shared/animationVariants';
import { DataFreshness } from '../../shared/DataFreshness';
import { PriorityAlertBar, type PriorityAlert } from '../components/PriorityAlertBar';
/** Days of inactivity before a student is flagged as "long inactive" (MN-02) */
const LONG_INACTIVE_DAYS = 14;
const LONG_INACTIVE_MS = LONG_INACTIVE_DAYS * 24 * 60 * 60 * 1000;
// Simple animated number component (SC-3: added cancelAnimationFrame cleanup)
function AnimatedNumber({
  value
}: {
  value: number;
}) {
  const [displayValue, setDisplayValue] = useState(0);
  useEffect(() => {
    let startTime: number;
    let rafId: number;
    const duration = 1000; // 1 second
    const startValue = 0;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      // easeOutQuart
      const easeProgress = 1 - Math.pow(1 - progress, 4);
      setDisplayValue(Math.floor(startValue + easeProgress * (value - startValue)));
      if (progress < 1) {
        rafId = requestAnimationFrame(step);
      } else {
        setDisplayValue(value);
      }
    };
    rafId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafId);
  }, [value]);
  return <>{displayValue}</>;
}
interface TodayPageProps {
  students: StudentData[];
  selectedGroup: GroupId;
  getModuleMastery: (learnerId: string, moduleId: string) => number;
  onSelectStudent: (studentId: string) => void;
  onNavigate: (route: string) => void;
  classroomName?: string;
}
export function TodayPage({
  students,
  selectedGroup,
  getModuleMastery,
  onSelectStudent,
  onNavigate,
  classroomName
}: TodayPageProps) {
  const [isAddStudentOpen, setIsAddStudentOpen] = useState(false);
  const [lastLoadedAt, setLastLoadedAt] = useState(() => new Date());
  const stats = useMemo(() => {
    if (students.length === 0) return null;
    const strugglingStudents: Array<{
      student: StudentData;
      insight: DifferentiationInsight;
    }> = [];
    let studentsPracticedThisWeek = 0;
    let totalMasterySum = 0;
    let totalMasteryCount = 0;
    const moduleMasterySums: Record<string, {
      sum: number;
      count: number;
    }> = {};
    MODULES.forEach(m => {
      moduleMasterySums[m.id] = {
        sum: 0,
        count: 0
      };
    });
    const allowedTiers = getAllowedTiersForGroup(selectedGroup);
    const familyStatsMap: Record<string, {
      familyId: string;
      moduleId: string;
      label: string;
      totalMastery: number;
      practicedCount: number;
      strugglingCount: number;
    }> = {};
    const inactiveStudents = students.filter(student => {
      const weeklyActivity = getWeeklyActivityForLearner(student.data.questionStates || {});
      return weeklyActivity.filter(d => d.practiced).length === 0;
    }).map(student => {
      let lastActive = 0;
      if (student.data.questionStates) {
        for (const state of Object.values(student.data.questionStates)) {
          if (state.lastAttemptDate > lastActive) {
            lastActive = state.lastAttemptDate;
          }
        }
      }
      return {
        student,
        lastActive
      };
    }).sort((a, b) => b.lastActive - a.lastActive);
    // Group context is needed for insights, we can extract it from the first student's data if available
    // or fallback to 6. In a real app, selectedGroup would be passed as a prop.
    // Since TodayPage doesn't receive selectedGroup, we'll try to infer it or default to 6.
    // Actually, we can just pass 6 as a fallback, but let's try to get it from the first student's groupVersion if possible.
    // Wait, getStudentInsights needs a GroupId. Let's just use 6 as a fallback since we don't have it in props.
    // A better way: TodayPage is rendered inside TeacherShellV2 which has selectedGroup.
    // Let's add selectedGroup to TodayPageProps.
    students.forEach(student => {
      let hasPracticed = false;
      MODULES.forEach(module => {
        const mastery = getModuleMastery(student.id, module.id);
        if (mastery > 0) {
          hasPracticed = true;
          totalMasterySum += mastery;
          totalMasteryCount++;
          moduleMasterySums[module.id].sum += mastery;
          moduleMasterySums[module.id].count++;
        }
      });
      if (hasPracticed) {
        // Use the selectedGroup from props
        const insights = getStudentInsights(student.data, selectedGroup, 1);
        if (insights.length > 0 && insights[0].mastery < V11_CONFIG.STRUGGLING_THRESHOLD) {
          strugglingStudents.push({
            student,
            insight: insights[0]
          });
        }
      }
      const weeklyActivity = getWeeklyActivityForLearner(student.data.questionStates || {});
      if (weeklyActivity.some(d => d.practiced)) {
        studentsPracticedThisWeek++;
      }
      // Calculate family stats for class-wide weak spots
      MODULES.forEach(module => {
        const families = getFamiliesForModule(module.id);
        const relevantFamilies = families.filter(f => allowedTiers.has(f.tier));
        relevantFamilies.forEach(f => {
          const key = getFamilyMasteryKey(f.familyId, selectedGroup);
          const fm = student.data.familyMastery?.[key];
          if (fm && fm.attempts > 0) {
            if (!familyStatsMap[f.familyId]) {
              familyStatsMap[f.familyId] = {
                familyId: f.familyId,
                moduleId: module.id,
                label: humanizeFamilyId(f.familyId),
                totalMastery: 0,
                practicedCount: 0,
                strugglingCount: 0
              };
            }
            familyStatsMap[f.familyId].practicedCount++;
            familyStatsMap[f.familyId].totalMastery += fm.masteryScore;
            if (fm.masteryScore < V11_CONFIG.STRUGGLING_THRESHOLD) {
              familyStatsMap[f.familyId].strugglingCount++;
            }
          }
        });
      });
    });
    const classWideWeakSpots = Object.values(familyStatsMap).map(f => ({
      ...f,
      avgMastery: Math.round(f.totalMastery / f.practicedCount)
    })).filter(f => f.strugglingCount >= 2) // Only show if multiple students struggle
    .sort((a, b) => {
      if (b.strugglingCount !== a.strugglingCount) {
        return b.strugglingCount - a.strugglingCount;
      }
      return a.avgMastery - b.avgMastery;
    }).slice(0, 5);
    const classAverageMastery = totalMasteryCount > 0 ? Math.round(totalMasterySum / totalMasteryCount) : 0;
    let strongestModule = MODULES[0];
    let weakestClassModule = MODULES[0];
    let highestAvg = -1;
    let lowestAvg = 101;
    MODULES.forEach(m => {
      const mStats = moduleMasterySums[m.id];
      if (mStats.count > 0) {
        const avg = mStats.sum / mStats.count;
        if (avg > highestAvg) {
          highestAvg = avg;
          strongestModule = m;
        }
        if (avg < lowestAvg) {
          lowestAvg = avg;
          weakestClassModule = m;
        }
      }
    });
    if (highestAvg === -1) {
      strongestModule = MODULES[0];
      weakestClassModule = MODULES[0];
    }
    return {
      strugglingStudents: strugglingStudents.sort((a, b) => a.insight.mastery - b.insight.mastery).slice(0, 6),
      totalStruggling: strugglingStudents.length,
      inactiveStudents,
      studentsPracticedThisWeek,
      classAverageMastery,
      /*
       Er is nog geen enkele beheersingsmeting in deze klas. Dat is iets anders
       dan "de klas staat op 0%": dat tweede is een uitspraak over de kinderen,
       en dit scherm deed die uitspraak onvoorwaardelijk — met een sterkste en
       zwakste vak erbij, die dan allebei op `MODULES[0]` uitkwamen en dus
       willekeurig waren.
        Een leerkracht die een nieuwe klas opent, leest dus dat zijn klas overal
       op nul staat en dat rekenen het zwakste vak is. Beide onwaar, beide
       overtuigend gebracht.
       */
      hasAnyMeasurement: totalMasteryCount > 0,
      strongestModule,
      weakestClassModule,
      highestAvg,
      lowestAvg,
      classWideWeakSpots
    };
  }, [students, getModuleMastery, selectedGroup]);
  const alerts = useMemo(() => {
    if (!stats) return [];
    const newAlerts: PriorityAlert[] = [];
    stats.strugglingStudents.forEach(({
      student,
      insight
    }) => {
      newAlerts.push({
        id: `struggle-${student.id}`,
        tone: 'urgent',
        icon: AlertTriangle,
        message: `${student.name} loopt vast op ${insight.familyName}`,
        onClick: () => onSelectStudent(student.id)
      });
    });
    if (newAlerts.length < 5) {
      stats.inactiveStudents.forEach(({
        student,
        lastActive
      }) => {
        if (newAlerts.length >= 5) return;
        if (lastActive === 0 || Date.now() - lastActive > LONG_INACTIVE_MS) {
          newAlerts.push({
            id: `inactive-${student.id}`,
            tone: 'warning',
            icon: Clock,
            message: `${student.name} is al ${lastActive === 0 ? 'nog nooit' : formatRelativeTime(lastActive)} niet actief`,
            onClick: () => onSelectStudent(student.id)
          });
        }
      });
    }
    return newAlerts;
  }, [stats, onSelectStudent]);
  const hour = new Date().getHours();
  let greeting = 'Goedenavond';
  let greetingIcon = '🌙';
  if (hour >= 6 && hour < 12) {
    greeting = 'Goedemorgen';
    greetingIcon = '☀️';
  } else if (hour >= 12 && hour < 18) {
    greeting = 'Goedemiddag';
    greetingIcon = '🌤️';
  }
  const dateString = new Date().toLocaleDateString('nl-NL', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
  if (students.length === 0) {
    return <PageLayout title="Vandaag" description="Overzicht van de klas" actions={<Button onClick={() => setIsAddStudentOpen(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl text-sm font-bold transition-all flex items-center gap-2" data-id="element-10881" data-mp="25out1pn3vtm4">
          
            <Plus className="w-4 h-4" data-id="element-10882" data-mp="u55ppkfp74hi" />
            Leerling Toevoegen
          </Button>} data-id="element-10880" data-mp="25lyxg7yqdh0u">
        
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.4
      }} data-id="element-10883" data-mp="yvujx7irifkc">
          
          <EmptyState title="Nog geen leerlingen" message="Je klas is nog leeg. Voeg leerlingen toe om hun voortgang te volgen en inzicht te krijgen in hun prestaties." emotion="thinking" actionLabel="Naar Mijn Klas" onAction={() => onNavigate('class')} colorScheme="indigo" data-id="element-10884" data-mp="18u1yjjpkwlj6" />
          
        </motion.div>
      </PageLayout>;
  }
  if (!stats) return null;
  const StrongestIcon = getModuleIcon(stats.strongestModule.icon);
  const WeakestIcon = getModuleIcon(stats.weakestClassModule.icon);
  return <PageLayout title="Vandaag" description={classroomName ? `Actuele inzichten voor ${classroomName}` : 'Actuele inzichten en aandachtspunten'} data-id="element-10885" data-mp="qrldu2jvj8zv">
      
      <motion.div className="max-w-5xl mx-auto space-y-6 pb-12" variants={containerVariants} initial="hidden" animate="visible" data-id="element-10886" data-mp="lttl56benium">
        
        <div className="flex justify-end mb-[-12px]" data-id="element-10887" data-mp="zpygry1u94lr">
          <DataFreshness updatedAt={lastLoadedAt} onRefresh={() => setLastLoadedAt(new Date())} data-id="element-10888" data-mp="vpwnac69bhhk" />
          
        </div>

        {/* Welcome Block */}
        <motion.div variants={itemVariants} className="bg-indigo-600 rounded-2xl p-4 text-white shadow-md relative overflow-hidden" data-id="element-10889" data-mp="14rdzhjgjmz0z">
          
          <div className="relative z-10 flex flex-col md:flex-row md:items-end justify-between gap-4" data-id="element-10890" data-mp="1jez2zehbz09f">
            <div data-id="element-10891" data-mp="1eiiegj67yjvt">
              <h1 className="text-2xl font-black mb-1" data-id="element-10892" data-mp="1ehz69u6df9uz">
                {greetingIcon} {greeting}!
              </h1>
              <p className="text-indigo-100 font-medium text-sm" data-id="element-10893" data-mp="1sem4e0a2862h">
                {classroomName || 'Mijn Klas'} •{' '}
                <span className="capitalize" data-id="element-10894" data-mp="273xbu2v470rm">{dateString}</span>
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-4 text-sm font-medium bg-white/10 px-4 py-2 rounded-xl backdrop-blur-sm border border-white/10" data-id="element-10895" data-mp="2c2iahxupxcdy">
              <div className="flex items-center gap-1.5" data-id="element-10896" data-mp="1y2ppmj5vhqdw">
                <Users className="w-4 h-4 text-indigo-200" data-id="element-10897" data-mp="223t2qxwjd8bf" />
                <span data-id="element-10898" data-mp="226fbv1z2rvvr">{students.length} leerlingen</span>
              </div>
              <div className="w-1 h-1 rounded-full bg-indigo-300 hidden sm:block" data-id="element-10899" data-mp="1obv2zdopsorx"></div>
              <div className="flex items-center gap-1.5" data-id="element-10900" data-mp="1t5h4qatcb88y">
                <TrendingUp className="w-4 h-4 text-emerald-300" data-id="element-10901" data-mp="1efi8wep9c8wn" />
                <span data-id="element-10902" data-mp="1ed5h79tp6k6e">{stats.studentsPracticedThisWeek} actief</span>
              </div>
              <div className="w-1 h-1 rounded-full bg-indigo-300 hidden sm:block" data-id="element-10903" data-mp="1jc0aj5pajr4r"></div>
              <div className="flex items-center gap-1.5" data-id="element-10904" data-mp="15etrrb4khsfj">
                <AlertTriangle className={`w-4 h-4 ${stats.totalStruggling > 0 ? 'text-rose-300' : 'text-indigo-200'}`} data-id="element-10905" data-mp="1adwrcg1s36g4" />
                
                {/* Zonder meting is "0 aandachtspunten" een geruststelling die
                    nergens op rust — precies de verkeerde kant om te falen. */}
                <span data-id="element-10906" data-mp="sm45b2gphvjw">
                  {stats.hasAnyMeasurement ? `${stats.totalStruggling} aandachtspunten` : 'nog niets gemeten'}
                </span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Priority Alerts */}
        <motion.div variants={itemVariants} data-id="element-10907" data-mp="jjuwch136a8m">
          <PriorityAlertBar alerts={alerts} maxVisible={3} data-id="element-10908" data-mp="emuy2sahabs2" />
        </motion.div>

        {/* Quick Actions Row */}
        <motion.div variants={itemVariants} className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-10909" data-mp="9ot7lmaq3a6k">
          
          <button onClick={() => onNavigate('homework')} className="flex items-center gap-3 bg-white p-4 rounded-xl border border-slate-200 shadow-sm hover:shadow-md hover:border-indigo-300 transition-all group text-left" data-id="element-10910" data-mp="5o0yct1up5aa">
            
            <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center group-hover:bg-blue-600 group-hover:text-white transition-colors" data-id="element-10911" data-mp="3hy3wvwdcj3cy">
              <BookOpen className="w-5 h-5" data-id="element-10912" data-mp="3mvcd3rexoxbk" />
            </div>
            <div data-id="element-10913" data-mp="3royicyirpyxc">
              <div className="font-bold text-slate-900 group-hover:text-blue-700 transition-colors" data-id="element-10914" data-mp="3rofdaie7v50q">
                Huiswerk
              </div>
              <div className="text-xs text-slate-500 font-medium" data-id="element-10915" data-mp="2yz866v0p573v">
                Naar overzicht
              </div>
            </div>
          </button>
          <button onClick={() => onNavigate('assessments')} className="flex items-center gap-3 bg-white p-4 rounded-xl border border-slate-200 shadow-sm hover:shadow-md hover:border-indigo-300 transition-all group text-left" data-id="element-10916" data-mp="33yur7qmrnrn8">
            
            <div className="w-10 h-10 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center group-hover:bg-indigo-600 group-hover:text-white transition-colors" data-id="element-10917" data-mp="n9p9ih6mz9go">
              <ClipboardCheck className="w-5 h-5" data-id="element-10918" data-mp="sav4klnrr8td" />
            </div>
            <div data-id="element-10919" data-mp="2oalpqhz3gvqo">
              <div className="font-bold text-slate-900 group-hover:text-indigo-700 transition-colors" data-id="element-10920" data-mp="2oa3p795efvcy">
                Nieuwe toets
              </div>
              <div className="text-xs text-slate-500 font-medium" data-id="element-10921" data-mp="2yambsboclpyk">
                Toets inplannen
              </div>
            </div>
          </button>
          <button onClick={() => onNavigate('reports')} className="flex items-center gap-3 bg-white p-4 rounded-xl border border-slate-200 shadow-sm hover:shadow-md hover:border-indigo-300 transition-all group text-left" data-id="element-10922" data-mp="2t8x0cux7ra9y">
            
            <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center group-hover:bg-emerald-600 group-hover:text-white transition-colors" data-id="element-10923" data-mp="2fbqlhbfm1vq0">
              <FileText className="w-5 h-5" data-id="element-10924" data-mp="1muxr5qykh30x" />
            </div>
            <div data-id="element-10925" data-mp="1mt35gafh16vs">
              <div className="font-bold text-slate-900 group-hover:text-emerald-700 transition-colors" data-id="element-10926" data-mp="1msl08oatzibq">
                Rapporten
              </div>
              <div className="text-xs text-slate-500 font-medium" data-id="element-10927" data-mp="3ti5674m8u8dp">
                Resultaten bekijken
              </div>
            </div>
          </button>
          <button onClick={() => onNavigate('management')} className="flex items-center gap-3 bg-white p-4 rounded-xl border border-slate-200 shadow-sm hover:shadow-md hover:border-indigo-300 transition-all group text-left" data-id="element-10928" data-mp="3kii2ud9yesi0">
            
            <div className="w-10 h-10 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center group-hover:bg-amber-600 group-hover:text-white transition-colors" data-id="element-10929" data-mp="g5cwc9g2otzi">
              <UserPlus className="w-5 h-5" data-id="element-10930" data-mp="73vfupyr6qaa" />
            </div>
            <div data-id="element-10931" data-mp="75pujmjflm5x">
              <div className="font-bold text-slate-900 group-hover:text-amber-700 transition-colors" data-id="element-10932" data-mp="c2ombyfmhh0y">
                Leerling toevoegen
              </div>
              <div className="text-xs text-slate-500 font-medium" data-id="element-10933" data-mp="2rs9hkck4frz7">
                Klas beheren
              </div>
            </div>
          </button>
        </motion.div>

        {/* Class Overview Card */}
        <motion.div variants={itemVariants} data-id="element-10934" data-mp="3nkbwfje56tzi">
          <Card className="bg-white border-slate-200 shadow-sm overflow-hidden p-5" data-id="element-10935" data-mp="3jhnkv3y1n8yu">
            <div className="flex items-center gap-2 mb-4" data-id="element-10936" data-mp="3eib1wuaamm1j">
              <TrendingUp className="w-5 h-5 text-indigo-600" data-id="element-10937" data-mp="3eiuhi4ch0yce" />
              <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wider" data-id="element-10938" data-mp="39k14ed13j9cr">
                Klas Overzicht
              </h2>
            </div>
            {!stats.hasAnyMeasurement ? <div className="rounded-xl bg-slate-50 border border-slate-200 p-5" data-id="element-10939" data-mp="62rc658tzlr8">
                <p className="font-bold text-slate-800" data-id="element-10940" data-mp="1bssqwsjq7jr">
                  Nog geen beheersing gemeten
                </p>
                <p className="text-sm text-slate-600 leading-relaxed mt-1 max-w-xl" data-id="element-10941" data-mp="3silty0nca6oa">
                  Zodra je leerlingen hebben geoefend, staan hier het
                  klasgemiddelde en het sterkste en zwakste vak. Er staat nu
                  bewust geen 0% — dat zou lezen als een resultaat, en er is nog
                  niets gemeten.
                </p>
              </div> : <div className="grid grid-cols-1 md:grid-cols-3 gap-6 divide-y md:divide-y-0 md:divide-x divide-slate-100" data-id="element-10942" data-mp="wew0rnisajxl">
              <div className="flex flex-col" data-id="element-10943" data-mp="16a66al8e59g4">
                <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1" data-id="element-10944" data-mp="1694l3nxnv6ko">
                  Gemiddelde beheersing
                </div>
                <div className="flex items-end gap-2" data-id="element-10945" data-mp="1fbx7pdvweu46">
                  <div className="text-3xl font-black text-slate-900" data-id="element-10946" data-mp="1f9b2vm0e6spn">
                    <AnimatedNumber value={stats.classAverageMastery} data-id="element-10947" data-mp="1k95rtk0lx882" />%
                  </div>
                </div>
              </div>
              <div className="flex flex-col md:pl-6 pt-4 md:pt-0" data-id="element-10948" data-mp="1p3jjbbc5ev7g">
                <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1" data-id="element-10949" data-mp="1tzqrzbvxikt3">
                  Sterkste vak
                </div>
                <div className="flex items-center gap-2 mt-1" data-id="element-10950" data-mp="230ovlt2ggqb5">
                  <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center" data-id="element-10951" data-mp="22yvo6bk0q223">
                    <StrongestIcon className="w-4 h-4" data-id="element-10952" data-mp="37g5a6mm9qza0" />
                  </div>
                  <div data-id="element-10953" data-mp="37i9cx18fls8p">
                    <div className="font-bold text-slate-900" data-id="element-10954" data-mp="3cf9lpjwo193i">
                      {stats.strongestModule.title}
                    </div>
                    <div className="text-xs font-medium text-emerald-600" data-id="element-10955" data-mp="3ghmnjx8akmv0">
                      {stats.highestAvg > -1 ? Math.round(stats.highestAvg) : 0}
                      % beheersing
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex flex-col md:pl-6 pt-4 md:pt-0 cursor-pointer hover:bg-slate-50 rounded-xl p-2 -m-2 transition-colors" onClick={() => onNavigate('subjects')} data-id="element-10956" data-mp="3v5hgneq7ot8m">
                
                <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1" data-id="element-10957" data-mp="81jpmj6zecjv">
                  Zwakste vak
                </div>
                <div className="flex items-center gap-2 mt-1" data-id="element-10958" data-mp="d0e7nix4jx2x">
                  <div className="w-8 h-8 rounded-lg bg-rose-50 text-rose-600 flex items-center justify-center" data-id="element-10959" data-mp="1xj88qs89sbin">
                    <WeakestIcon className="w-4 h-4" data-id="element-10960" data-mp="1siauuruam3my" />
                  </div>
                  <div data-id="element-10961" data-mp="1ogpaklffin2q">
                    <div className="font-bold text-slate-900 group-hover:text-indigo-600 transition-colors" data-id="element-10962" data-mp="1oi0nbaqur55z">
                      {stats.weakestClassModule.title}
                    </div>
                    <div className="text-xs font-medium text-rose-600" data-id="element-10963" data-mp="2g7d8zpppxecp">
                      {stats.lowestAvg < 101 ? Math.round(stats.lowestAvg) : 0}%
                      beheersing
                    </div>
                  </div>
                </div>
              </div>
            </div>}
          </Card>
        </motion.div>

        {/* Class-wide Weak Spots */}
        {stats.classWideWeakSpots.length > 0 && <motion.section variants={itemVariants} data-id="element-10964" data-mp="2zy74yub4eacl">
            <div className="flex items-center justify-between mb-4" data-id="element-10965" data-mp="2v6785aqcjxk8">
              <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2" data-id="element-10966" data-mp="2v3uj6dm68mxo">
                <Target className="w-4 h-4 text-indigo-500" data-id="element-10967" data-mp="3j199m0ko02pm" />
                Klas-brede Zwakke Plekken
              </h2>
            </div>
            <Card className="border-slate-200 shadow-sm overflow-hidden bg-white" data-id="element-10968" data-mp="358t2ooon7kjf">
              <div className="grid grid-cols-[auto_1fr_auto_auto] gap-4 items-center px-4 py-3 bg-slate-50 border-b border-slate-200 text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-10969" data-mp="39axr9908hqz7">
                <div className="w-8" data-id="element-10970" data-mp="30baq8obf2n6o"></div>
                <div data-id="element-10971" data-mp="30ccczq1soy48">Onderdeel</div>
                <div className="text-right w-32" data-id="element-10972" data-mp="2mazzdla3x8li">Moeite mee</div>
                <div className="w-6" data-id="element-10973" data-mp="2mckwnint35n2"></div>
              </div>
              <motion.div className="divide-y divide-slate-100" variants={listContainerVariants} initial="hidden" animate="visible" data-id="element-10974" data-mp="2qbtmwdjn0ldv">
              
                {stats.classWideWeakSpots.map((spot, i) => {
              const moduleDef = MODULES.find(m => m.id === spot.moduleId);
              const IconComp = getModuleIcon(moduleDef?.icon || 'calculator');
              return <motion.div key={spot.familyId} variants={listItemVariants} onClick={() => onNavigate('subjects')} className={`grid grid-cols-[auto_1fr_auto_auto] gap-4 items-center px-4 py-3 cursor-pointer hover:bg-indigo-50/50 transition-colors group ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}`} data-id="element-10975" data-mp="251nyxxe1i60r" data-mpi={i}>
                    
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${moduleDef?.bgColor || 'bg-slate-100'} ${moduleDef?.color || 'text-slate-500'}`} data-id="element-10976" data-mp="2sqgys4xs8y0n">
                      
                        <IconComp className="w-4 h-4" data-id="element-10977" data-mp="31s6p17fxhqwu" />
                      </div>
                      <div className="min-w-0 flex flex-col gap-1" data-id="element-10978" data-mp="2wt3jzmzy7kov">
                        <div className="font-bold text-sm text-slate-900 truncate group-hover:text-indigo-700 transition-colors" data-id="element-10979" data-mp="2wqr1n8qa38ba">
                          {spot.label}
                        </div>
                        <div className="flex items-center gap-2" data-id="element-10980" data-mp="12gx8so7213wk">
                          <div className="w-24 h-1.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-10981" data-mp="14splfhxj1r5v">
                            <div className={`h-full rounded-full ${spot.avgMastery >= 75 ? 'bg-emerald-500' : spot.avgMastery >= 40 ? 'bg-indigo-400' : 'bg-rose-500'}`} style={{
                        width: `${Math.max(2, spot.avgMastery)}%`
                      }} data-id="element-10982" data-mp="18tro68qnklcl" />
                          
                          </div>
                          <span className="text-[10px] font-bold text-slate-400" data-id="element-10983" data-mp="l3f7a8dfje9v">
                            {spot.avgMastery}% gem.
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center justify-end w-32" data-id="element-10984" data-mp="zuedjcqslqqa">
                        <span className="font-bold text-xs text-rose-600 bg-rose-50 px-2 py-1 rounded-md" data-id="element-10985" data-mp="zwhbqk23mq63">
                          {spot.strugglingCount} van {spot.practicedCount} lln
                        </span>
                      </div>
                      <div className="text-slate-300 w-6 flex justify-end group-hover:text-indigo-500 transition-colors group-hover:translate-x-1 duration-200" data-id="element-10986" data-mp="3w0i5a8p8l7ta">
                        <ArrowRight className="w-4 h-4" data-id="element-10987" data-mp="3qz16qju85xfv" />
                      </div>
                    </motion.div>;
            })}
              </motion.div>
            </Card>
          </motion.section>}

        {/* Aandachtspunten is now covered by PriorityAlertBar above.
             Only show a compact "see all" affordance when overflow exists. */}
        {stats.totalStruggling > 3 && <motion.div variants={itemVariants} className="text-center -mt-2" data-id="element-10988" data-mp="3892zg5y948da">
            <button onClick={() => onNavigate('class')} className="text-sm font-bold text-indigo-600 hover:text-indigo-700 transition-colors inline-flex items-center gap-1.5 group" data-id="element-10989" data-mp="33c2tdv1ej5m6">
            
              Bekijk alle {stats.totalStruggling} leerlingen die aandacht nodig
              hebben
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-10990" data-mp="2pcl2na4jpia6" />
            </button>
          </motion.div>}

        {/* Inactive Students */}
        <motion.section variants={itemVariants} data-id="element-10991" data-mp="2jq0xohue290j">
          <div className="flex items-center justify-between mb-4" data-id="element-10992" data-mp="2jqsutx3mwesx">
            <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2" data-id="element-10993" data-mp="25qrtyhndd7dg">
              <Clock className="w-4 h-4 text-amber-500" data-id="element-10994" data-mp="2apma1btychtv" />
              Niet geoefend deze week
            </h2>
          </div>
          {stats.inactiveStudents.length > 0 ? <Card className="border-slate-200 shadow-sm overflow-hidden bg-white" data-id="element-10995" data-mp="32fr09r4e7z30">
              <div className="grid grid-cols-[auto_1fr_auto_auto] gap-4 items-center px-4 py-3 bg-slate-50 border-b border-slate-200 text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-10996" data-mp="2oom08nx454s5">
                <div className="w-8" data-id="element-10997" data-mp="2tlch3dgqs4em"></div>
                <div data-id="element-10998" data-mp="3hc8d6ms8dlro">Naam</div>
                <div className="text-right w-32" data-id="element-10999" data-mp="3hbynz1f0fin1">Inactief sinds</div>
                <div className="w-6" data-id="element-11000" data-mp="3m51h58fvkq2p"></div>
              </div>
              <motion.div className="divide-y divide-slate-100" variants={listContainerVariants} initial="hidden" animate="visible" data-id="element-11001" data-mp="3vis4w5wak9ha">
              
                {stats.inactiveStudents.slice(0, 5).map(({
              student,
              lastActive
            }, i) => {
              const inactiveLabel = lastActive > 0 ? formatRelativeTime(lastActive) : 'Nog nooit geoefend';
              return <motion.div key={student.id} variants={listItemVariants} onClick={() => onSelectStudent(student.id)} className={`grid grid-cols-[auto_1fr_auto_auto] gap-4 items-center px-4 py-2.5 cursor-pointer hover:bg-indigo-50/50 transition-colors group ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}`} data-id="element-11002" data-mp="3cjxqiy0qpb1n" data-mpi={i}>
                    
                        <div className="relative" data-id="element-11003" data-mp="3hir38o87cy0r">
                          <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${getAvatarGradient(student.name)} flex items-center justify-center text-white font-bold text-sm shadow-sm`} data-id="element-11004" data-mp="3hl3i2sinys9l">
                        
                            {student.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white bg-amber-500 shadow-sm" role="img" aria-label="Inactief deze week" data-id="element-11005" data-mp="2valgcieveu3p" />
                      
                        </div>
                        <div className="min-w-0" data-id="element-11006" data-mp="351gbkca3h41a">
                          <div className="font-bold text-sm text-slate-900 truncate group-hover:text-indigo-700 transition-colors" data-id="element-11007" data-mp="3529ef25xn9e5">
                            {student.name}
                          </div>
                        </div>
                        <div className="flex items-center justify-end w-32" data-id="element-11008" data-mp="3izxyi7s6eci5">
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-md ${lastActive === 0 ? 'bg-rose-50 text-rose-600' : Date.now() - lastActive > LONG_INACTIVE_MS ? 'bg-rose-50 text-rose-600' : 'bg-slate-100 text-slate-600'}`} data-id="element-11009" data-mp="3nz0s8w2pgwat">
                        
                            {inactiveLabel}
                          </span>
                        </div>
                        <div className="text-slate-300 w-6 flex justify-end group-hover:text-indigo-500 transition-colors group-hover:translate-x-1 duration-200" data-id="element-11010" data-mp="1m3js81owv2vs">
                          <ArrowRight className="w-4 h-4" data-id="element-11011" data-mp="1ccp750nei9bx" />
                        </div>
                      </motion.div>;
            })}
              </motion.div>
              {stats.inactiveStudents.length > 5 && <div className="p-3 bg-slate-50 border-t border-slate-100 text-center" data-id="element-11012" data-mp="24z9xbxs54fdw">
                  <button onClick={() => onNavigate('class')} className="text-xs font-bold text-slate-500 hover:text-indigo-600 uppercase tracking-wider transition-colors inline-flex items-center gap-1 group" data-id="element-11013" data-mp="202jifds2npu2">
                
                    + {stats.inactiveStudents.length - 5} meer bekijken
                    <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" data-id="element-11014" data-mp="job426hggmti" />
                  </button>
                </div>}
            </Card> : <Card className="p-6 text-center border-emerald-100 bg-emerald-50/50 shadow-sm overflow-hidden flex flex-col items-center justify-center" data-id="element-11015" data-mp="3aiev12eyx7va">
              <DjayNarrator size="sm" emotion="happy" data-id="element-11016" data-mp="35kn1k3yoddoz" />
              <p className="text-emerald-800 font-bold mt-3" data-id="element-11017" data-mp="3k7yfgcibx9s9">
                Iedereen heeft deze week geoefend!
              </p>
            </Card>}
        </motion.section>
      </motion.div>
    </PageLayout>;
}